import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { recebimentoService, relatoriosComprasService, type FiltrosRecebimento }
  from '../services/recebimentoService';
import type {
  AgrupamentoFluxoCaixa,
  AplicarAjustePrecoRequest,
  ConfirmarRecebimentoRequest,
  ManifestarDfeRequest,
} from '../types';

/**
 * Hook do fluxo de Recebimento com XML — Iteração 1.
 * Expõe apenas a importação (upload + parse).
 */
export const useImportarXmlNfe = () => {
  const importar = useMutation({
    mutationFn: ({ arquivo, cnpjDestinatario }: { arquivo: File; cnpjDestinatario?: string }) =>
      recebimentoService.importarXml(arquivo, cnpjDestinatario),
  });

  return {
    importar:    importar.mutateAsync,
    isImporting: importar.isPending,
    staging:     importar.data,
    erro:        importar.error,
    reset:       importar.reset,
  };
};

/**
 * Hook do preview de recebimento — Iteração 2.
 * Faz upload + matching contra banco e retorna RecebimentoPreview.
 */
export const usePreviewRecebimento = () => {
  const previewMut = useMutation({
    mutationFn: (p: { arquivo: File; cnpjDestinatario?: string; compraOrdemId?: number }) =>
      recebimentoService.preview(p.arquivo, {
        cnpjDestinatario: p.cnpjDestinatario,
        compraOrdemId:    p.compraOrdemId,
      }),
  });

  return {
    preview:     previewMut.mutateAsync,
    isPreviewing: previewMut.isPending,
    data:         previewMut.data,
    erro:         previewMut.error,
    reset:        previewMut.reset,
  };
};

/**
 * Hook de confirmação do recebimento — Iteração 4.
 * Persiste o recebimento + movimenta estoque + gera ContaPagar.
 */
export const useConfirmarRecebimento = () => {
  const mut = useMutation({
    mutationFn: (request: ConfirmarRecebimentoRequest) =>
      recebimentoService.confirmar(request),
  });

  return {
    confirmar:   mut.mutateAsync,
    isConfirming: mut.isPending,
    data:         mut.data,
    erro:         mut.error,
    reset:        mut.reset,
  };
};

/**
 * Preview do ajuste de preço pós-recebimento (GET).
 */
export const useAjustePrecoPreview = (recebimentoId?: number, empresaId?: number) =>
  useQuery({
    queryKey: ['ajuste-preco', recebimentoId, empresaId],
    queryFn:  () => recebimentoService.ajustePrecoPreview(recebimentoId!, empresaId),
    enabled:  !!recebimentoId,
  });

/**
 * Aplica os preços escolhidos no recebimento.
 */
export const useAplicarAjustePreco = () => {
  const mut = useMutation({
    mutationFn: (p: { recebimentoId: number; request: AplicarAjustePrecoRequest }) =>
      recebimentoService.ajustePrecoAplicar(p.recebimentoId, p.request),
  });

  return {
    aplicar:   mut.mutateAsync,
    isApplying: mut.isPending,
    data:       mut.data,
    erro:       mut.error,
  };
};

/**
 * Histórico de manifestações DFe deste recebimento (GET).
 */
export const useManifestacoes = (recebimentoId?: number) =>
  useQuery({
    queryKey: ['manifestacoes', recebimentoId],
    queryFn:  () => recebimentoService.listarManifestacoes(recebimentoId!),
    enabled:  !!recebimentoId,
  });

/**
 * Envia manifestação DFe (POST).
 */
/**
 * Lista paginada de recebimentos (GET).
 */
export const useRecebimentos = (pagina = 1, tamanhoPagina = 20, filtros: FiltrosRecebimento = {}) =>
  useQuery({
    queryKey: ['recebimentos', pagina, tamanhoPagina, filtros],
    queryFn:  () => recebimentoService.listar(pagina, tamanhoPagina, filtros),
    placeholderData: (anterior) => anterior, // a tabela não pisca a cada letra da busca
  });

/**
 * Detalhe de um recebimento (GET).
 */
export const useRecebimentoDetalhe = (id?: number) =>
  useQuery({
    queryKey: ['recebimento', id],
    queryFn:  () => recebimentoService.obter(id!),
    enabled:  !!id && id > 0,
  });

export const useManifestar = () => {
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: (p: {
      recebimentoId: number;
      request: ManifestarDfeRequest;
      empresaId?: number;
    }) => recebimentoService.manifestar(p.recebimentoId, p.request, p.empresaId),
    onSuccess: (_, p) =>
      qc.invalidateQueries({ queryKey: ['manifestacoes', p.recebimentoId] }),
  });

  return {
    manifestar:    mut.mutateAsync,
    isManifesting: mut.isPending,
    resultado:     mut.data,
    erro:          mut.error,
  };
};

/**
 * Download XML SEFAZ por chave (stub).
 */
export const useBaixarXmlSefaz = () => {
  const mut = useMutation({
    mutationFn: (chaveNfe: string) => recebimentoService.baixarXmlSefaz(chaveNfe),
  });
  return {
    baixar:      mut.mutateAsync,
    isBaixando:  mut.isPending,
    resultado:   mut.data,
    erro:        mut.error,
    reset:       mut.reset,
  };
};

/**
 * Relatório de Fluxo de Caixa Compras (Fase 3 · Iter 3.1).
 */
export const useFluxoCaixaCompras = (params: {
  dataInicio?: string;
  dataFim?: string;
  fornecedorId?: number;
  agrupamento?: AgrupamentoFluxoCaixa;
  apenasAbertos?: boolean;
}) =>
  useQuery({
    queryKey: ['compras-fluxo-caixa', params],
    queryFn:  () => relatoriosComprasService.fluxoCaixa(params),
  });

/**
 * Spend Analytics (Fase 3 · Iter 3.2).
 */
export const useSpendAnalytics = (params: {
  dataInicio?: string;
  dataFim?: string;
  fornecedorId?: number;
  grupoProdutoId?: number;
}) =>
  useQuery({
    queryKey: ['compras-spend', params],
    queryFn:  () => relatoriosComprasService.spendAnalytics(params),
  });

/**
 * Performance de Fornecedores · OTIF (Fase 3 · Iter 3.3).
 */
export const usePerformanceFornecedores = (params: {
  dataInicio?: string;
  dataFim?: string;
  fornecedorId?: number;
}) =>
  useQuery({
    queryKey: ['compras-performance-fornec', params],
    queryFn:  () => relatoriosComprasService.performanceFornecedores(params),
  });

/**
 * Relatório analítico de Pedidos de Compra (Fase 3 · Iter 3.3).
 */
export const useRelatorioPos = (params: {
  dataInicio?: string;
  dataFim?: string;
  fornecedorId?: number;
  status?: string;
}) =>
  useQuery({
    queryKey: ['compras-relatorio-pos', params],
    queryFn:  () => relatoriosComprasService.pedidos(params),
  });
